import { computed, reactive } from "vue";
import { CONFIRM } from "@/stores/constants";
import {
  useUpdateLocationGroups,
  type LocationGroup,
  useLocationGroups,
  useAllLocationGroups,
} from "@/api/locationGroups";
import type { ValueOf } from "@/types/utility";
import { useI18n } from "vue-i18n";
import type { Location } from "@/api/locations";

type ChildrenOf<T> = {
  [Property in keyof T]: string;
};

export const useLocationGroupsSettings = (callback?: () => void) => {
  const locationGroupsSaveStatus = reactive<Partial<ChildrenOf<LocationGroup>>>(
    {}
  );

  const updateLocationGroups = useUpdateLocationGroups(() => {
    Object.keys(locationGroupsSaveStatus).forEach((key) => {
      const setting = key as keyof LocationGroup;
      locationGroupsSaveStatus[setting] = CONFIRM.SUCCESS;
    });
    callback?.();
  });

  const updateLocationGroup = (
    setting: keyof LocationGroup,
    value: Partial<ValueOf<LocationGroup>>
  ) => {
    updateLocationGroups.mutate({ [setting]: value });
    locationGroupsSaveStatus[setting] = CONFIRM.PENDING;
  };

  const { data: locationGroupsData } = useLocationGroups();
  const { data: allLocationGroupsData } = useAllLocationGroups();
  const { t } = useI18n();

  const locationGroups = computed(() => locationGroupsData.value ?? []);
  const allLocationGroups = computed(() => allLocationGroupsData.value ?? []);

  const parentGroups = computed(
    () => locationGroups.value?.filter((group) => !group.invisible)
  );
  const singleGroups = computed(
    () => locationGroups.value?.filter((group) => group.invisible)
  );

  const getLocationsByLocationGroups = (searchTerm: string) => {
    const searchString = searchTerm.toLowerCase().trim();
    const results: { name: string; locations: Location[] }[] = [];
    const singleLocations: Location[] = [];

    locationGroups.value.forEach((locationGroup) => {
      const lowerLocationGroupName = locationGroup.name.toLowerCase();

      if (locationGroup.invisible) {
        if (!searchString || lowerLocationGroupName.includes(searchString)) {
          singleLocations.push(...locationGroup.locations);
        }

        return;
      }

      const filteredLocation = locationGroup.locations.filter((location) =>
        location.name.toLowerCase().includes(searchString)
      );

      if (filteredLocation.length > 0) {
        return results.push({
          name: locationGroup.name,
          locations: filteredLocation,
        });
      }

      if (!searchString || lowerLocationGroupName.includes(searchString)) {
        return results.push({
          name: locationGroup.name,
          locations: locationGroup.locations,
        });
      }
    });

    if (singleLocations.length) {
      results.push({
        name: t("employees.form.singleLocationsGrouping"),
        locations: singleLocations?.sort(
          (curLocation: Location, nextLocation: Location) =>
            curLocation.name.localeCompare(nextLocation.name)
        ),
      });
    }

    return results;
  };

  const groupedLocationGroups = computed(() => {
    const locationGroupsVal = [];

    if (parentGroups.value.length) {
      locationGroupsVal.push({
        groups: parentGroups.value,
        grouping: t("employees.form.locationGroupsGrouping"),
      });
    }

    if (singleGroups.value.length) {
      locationGroupsVal.push({
        groups: singleGroups.value,
        grouping: t("employees.form.singleLocationsGrouping"),
      });
    }

    return locationGroupsVal;
  });

  return {
    locationGroupsSaveStatus,
    groupedLocationGroups,
    allLocationGroups,
    locationGroups,
    parentGroups,
    singleGroups,
    updateLocationGroup,
    getLocationsByLocationGroups,
  };
};
